import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';

/**
 * LINKING A BLOCK THAT ALREADY HAS A QUESTION BANK — §7.1's three-way offer.
 *
 * An earlier draft of the design said "refuse". That was right about never discarding
 * silently and wrong about what to offer instead, and the case that showed it is the one this
 * feature exists for: an exam that already encodes its scenarios as per-block versions,
 * because versions were the only tool there was. Refusing makes the migration a rebuild.
 *
 * ## The question collapses either way, so the choice is about the ANSWERS
 *
 * A block holds one question, and a follower has no bank to pick from, so version 1's question
 * becomes the block's whichever button is pressed. What actually differs is where each
 * scenario's answer key comes from: version *i*'s, paired by position, or version 1's for all
 * of them. An earlier pass offered "pair them up" against "discard the bank and start empty",
 * which read as two different operations and looked identical in the first place anyone
 * checks, since scenario 1 gets version 1's answers under both.
 *
 * A migrated row comes back marked unreviewed, because nothing anywhere says version 2 was
 * scenario 2. The flag is what makes the offer safe: it does not claim the mapping is right,
 * it saves the typing and asks the teacher to check.
 *
 * A COUNT MISMATCH IS STATED, never absorbed. Fewer versions than variants is harmless and
 * goes unmentioned. More means answer keys with nowhere to go, and the teacher is told how
 * many before anything is written — a silent drop is the truncation §5.2 already rejected.
 */
export function ScenarioRemapModal({ versionCount, variantCount, leaderName, onCancel, onRemap, onLinkEmpty }: {
  versionCount: number;
  variantCount: number;
  leaderName: string;
  onCancel: () => void;
  onRemap: () => void;
  onLinkEmpty: () => void;
}) {
  const { t } = useTranslation();
  /** Answer sets with no scenario to land on. Stated, never absorbed. */
  const surplus = Math.max(0, versionCount - variantCount);
  return (
    <ModalShell
      title={t('blocks.scenario.remapTitle')}
      onClose={onCancel}
      maxWidth='max-w-sm'
      bottomSheetOnPhone
      icon={<AlertTriangle aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-warning-fg' />}
    >
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col gap-2 text-sm text-ws-muted'>
          <p>
            <strong className='font-semibold text-ws-text'>{leaderName}</strong>
            {' '}
            {t('blocks.scenario.remapCounts', { variants: variantCount, versions: versionCount })}
          </p>
          {/* What linking costs, before any button is pressed. The question collapses to
              version 1 either way, so it is stated once here rather than twice below. */}
          <p>{t('blocks.scenario.remapDrops', { count: versionCount - 1 })}</p>
        </div>
        {/*
          * The choice is ONLY about the answers.
          *
          * An earlier pass offered "pair them up" against "discard the bank and start empty",
          * which read as two different operations and looked identical in the one place a
          * teacher checks first: scenario 1 gets version 1's answers under both. Naming what
          * actually differs — migrate each version's answers, or give every scenario version
          * 1's — is the whole fix, and each button says its own consequence.
          */}
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <Button variant='primary' onClick={onRemap} className='w-full'>
              {t('blocks.scenario.remapMigrate')}
            </Button>
            <p className='text-xs text-ws-muted'>
              {surplus > 0
                ? t('blocks.scenario.remapMigrateSurplus', { count: surplus, from: variantCount + 1, to: versionCount })
                : t('blocks.scenario.remapMigrateNote')}
            </p>
          </div>
          <div className='flex flex-col gap-1'>
            <Button onClick={onLinkEmpty} className='w-full'>
              {t('blocks.scenario.remapReset')}
            </Button>
            <p className='text-xs text-ws-muted'>{t('blocks.scenario.remapResetNote')}</p>
          </div>
          <Button variant='subtle' onClick={onCancel} className='w-full'>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
